import { useTranslation } from 'react-i18next';

import useCurrency from 'hooks/useCurreny';
import { cn } from 'utils/cn';
import { useCroptimizer } from 'features/Croptimizer/providers/CroptimizerContext';

const Title = ({ title }: { title: string }) => (
  <div className="font-bold mb-4 text-center">{title}</div>
);

const CropTableHeader = ({
  isPDF,
  pdfColumnWidth,
}: {
  isPDF?: boolean;
  pdfColumnWidth: { [key: string]: string };
}) => {
  const { t } = useTranslation();
  const { currencyGreen } = useCurrency();
  const { mtHa, onlyMt, onlyAc, kgHa } = useCroptimizer();

  const CropRotation = () => (
    <div
      className={cn('px-0.5', {
        [`${pdfColumnWidth['1']} flex flex-col`]: isPDF,
        'w-[160px] text-center': !isPDF,
      })}
    >
      <Title title={t('cropRotation')} />
      <div
        className={cn('text-secondaryText flex-grow', {
          'flex items-center justify-center': isPDF,
        })}
      >
        {t('twototen')}
      </div>
    </div>
  );

  const Crop = () => (
    <div
      className={cn('px-0.5', {
        [`${pdfColumnWidth['2']} flex flex-col`]: isPDF,
      })}
    >
      <Title title={t('crop')} />
      <div className="flex divide-x flex-grow">
        <div
          className={cn('w-[80px] text-secondaryText text-center px-0.5', {
            'w-1/2 flex items-center justify-center ': isPDF,
          })}
        >
          {t('yield')} ({mtHa})
        </div>

        <div
          className={cn('w-[80px] text-secondaryText flex text-center px-0.5', {
            'w-1/2 flex items-center justify-center ': isPDF,
          })}
        >
          {t('price')} ({currencyGreen}/{onlyMt})
        </div>
      </div>
    </div>
  );

  const SoilPh = () => (
    <div
      className={cn('px-0.5', {
        [`${pdfColumnWidth['3']} flex flex-col`]: isPDF,
      })}
    >
      <Title title={`${t('soil')} pH`} />
      <div className="flex-grow flex justify-between divide-x">
        <div
          className={cn(
            'text-secondaryText text-center px-0.5',
            { 'w-1/4 flex items-center justify-center ': isPDF },
            { 'w-[100px]': !isPDF },
          )}
        >
          {t('phNolimeapp')}
        </div>
        <div
          className={cn(
            'text-secondaryText text-center px-0.5',
            { 'w-1/4 flex items-center justify-center ': isPDF },
            { 'w-[100px]': !isPDF },
          )}
        >
          {t('limingStatus')}
        </div>
        <div
          className={cn(
            'text-secondaryText text-center px-0.5',
            { 'w-1/4 flex items-center justify-center ': isPDF },
            { 'w-[100px]': !isPDF },
          )}
        >
          {t('expect')} pH
        </div>
      </div>
    </div>
  );

  const YieldGains = () => (
    <div
      className={cn('px-0.5', {
        [`${pdfColumnWidth['2']} flex flex-col`]: isPDF,
      })}
    >
      <Title title={t('yieldGains')} />
      <div className="flex-grow flex justify-around divide-x">
        <div
          className={cn(
            'text-secondaryText flex justify-center w-[60px] px-0.5',
            { 'w-1/2 flex items-center ': isPDF },
          )}
        >
          %
        </div>
        <div
          className={cn(
            'text-secondaryText flex justify-center w-[60px] px-0.5',
            { 'w-1/2 flex items-center ': isPDF },
          )}
        >
          {currencyGreen}/{onlyAc}
        </div>
      </div>
    </div>
  );

  const NpkFertilizerDecrease = () => (
    <div
      className={cn('px-0.5', {
        [`${pdfColumnWidth['3']} flex flex-col`]: isPDF,
      })}
    >
      <Title
        title={`${t('npkFertilizerdecrease')} (${kgHa} ${t('perCrop')})`}
      />
      <div className="flex-grow flex justify-around divide-x">
        <div
          className={`text-secondaryText flex justify-center px-0.5 ${
            isPDF ? 'w-1/3 flex items-center ' : 'w-[100px]'
          }`}
        >
          N
        </div>
        <div
          className={`text-secondaryText flex justify-center px-0.5 ${
            isPDF ? 'w-1/3 flex items-center ' : 'w-[100px]'
          }`}
        >
          P2O5
        </div>
        <div
          className={`text-secondaryText flex justify-center px-0.5 ${
            isPDF ? 'w-1/3 flex items-center ' : 'w-[100px]'
          }`}
        >
          K2O
        </div>
      </div>
    </div>
  );

  const Npk = () => (
    <div
      className={cn('w-[100px] border-r px-0.5', {
        [`${pdfColumnWidth['1']} flex flex-col`]: isPDF,
      })}
    >
      <Title title={t('npk')} />
      <div
        className={cn('text-secondaryText text-center flex-grow', {
          'flex items-center justify-center': isPDF,
        })}
      >
        {t('gains')} ({currencyGreen}/{onlyAc})
      </div>
    </div>
  );

  const MonPs = () => (
    <div
      className={cn('w-[100px] border-r px-0.5', {
        [`${pdfColumnWidth['1']} flex flex-col`]: isPDF,
      })}
    >
      <Title title={t('monps')} />
      <div
        className={cn('text-secondaryText text-center flex-grow', {
          'flex items-center justify-center': isPDF,
        })}
      >
        {t('gains')} ({currencyGreen}/{onlyAc})
      </div>
    </div>
  );

  const TotalGains = () => (
    <div
      className={cn('w-[100px] border-r px-0.5', {
        [`${pdfColumnWidth['1']} flex flex-col`]: isPDF,
      })}
    >
      <Title title={t('totalGains')} />
      <div
        className={cn('text-secondaryText text-center break-words flex-grow', {
          'flex items-center justify-center': isPDF,
        })}
      >
        {t('rdtnpks')} ({currencyGreen}/{onlyAc})
      </div>
    </div>
  );

  return (
    <div
      className={cn('flex divide-x p-4 border-b text-sm', { 'text-xs': isPDF })}
    >
      <CropRotation />
      <Crop />
      <SoilPh />
      <YieldGains />
      <NpkFertilizerDecrease />
      <Npk />
      <MonPs />
      <TotalGains />
    </div>
  );
};

export default CropTableHeader;
